import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useParams, useOutletContext, Link as RouterLink, Outlet } from "react-router-dom";
import { serializeQuery } from "./url";

/**
 * A custom HOC to provide react-router-dom v6 hooks to class components.
 */
export const withRouter = (Component) => {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    let context = useOutletContext();

    /**
     * Helper to parse query string values into their correct primitive types.
     */
    const parseValue = (val) => {
      if (typeof val !== "string") return val;
      const lowerVal = val.toLowerCase();
      if (lowerVal === "true") return true;
      if (lowerVal === "false") return false;
      if (/^\d+$/.test(val)) return Number(val);
      return val;
    };

    /**
     * Parses the current location.search from a search string into a query object.
     * This restores the location.query functionality from legacy react-router v3
     * and prevents crashes due to old usage of query object in the location object.
     */
    const locationWithQuery = useMemo(() => {
      const query = {};
      const searchParams = new URLSearchParams(location.search);
      // Construct the query object from the search parameters.
      // Example: "?national_class=514&national_class=424&exparte_status=Other" -> { national_class: ["514", "424"], exparte_status: "Other" }
      searchParams.forEach((value, key) => {
        const parsedValue = parseValue(value);
        if (query[key]) {
          if (Array.isArray(query[key])) {
            query[key].push(parsedValue);
          } else {
            query[key] = [query[key], parsedValue];
          }
        } else {
          query[key] = parsedValue;
        }
      });
      return { ...location, query };
    }, [location]);

    /**
     * Enhances the navigate function to support objects with a 'query' property by converting it back to a search string.
     * This restores compatibility with legacy calls like router.push({ pathname, query })
     * and prevents crashes from old code together with `locationWithQuery()`.
     */
    const enhancedNavigate = useMemo(() => {
      const wrapNavigate = (navFunc) => (to, options) => {
        if (typeof to === "object" && to.query) {
          to.search = serializeQuery(to.query);
          delete to.query;
        }
        navFunc(to, options);
      };

      return {
        push: wrapNavigate(navigate),
        replace: wrapNavigate((to, opts) => navigate(to, { ...opts, replace: true }))
      };
    }, [navigate]);

    return (
      <Component
        {...props}
        {...context}
        location={locationWithQuery}
        navigate={navigate}
        params={params}
        router={{
          location: locationWithQuery,
          navigate,
          params,
          push: enhancedNavigate.push,
          replace: enhancedNavigate.replace,
          goBack: () => navigate(-1)
        }}
      />
    );
  }

  return ComponentWithRouterProp;
};

/**
 * A custom Link component that handles query parameter serialization.
 */
export const Link = ({ to, ...props }) => {
  let finalTo = to;
  if (typeof to === "object" && to !== null) {
    if (to.query) {
      finalTo = {
        ...to,
        search: serializeQuery(to.query)
      };
      delete finalTo.query;
    }
  }
  return <RouterLink to={finalTo} {...props} />;
};

Link.propTypes = {
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.object,
      search: PropTypes.string,
      state: PropTypes.object
    })
  ]).isRequired
};

export { Outlet };
